import { useState } from "react";
import { motion } from "framer-motion";
import { Quote, Trophy, Plane, Mic, BookOpen, X, Expand } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const C = (f) => `/assets/media/company/${f}.jpg`;
export const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};
const Eyebrow = ({ children, light }) => (
  <div className={`font-semibold text-[11px] tracking-[0.3em] uppercase mb-3 ${light ? "text-[var(--yellow)]" : "text-[var(--red)]"}`}>{children}</div>
);

export const FounderSection = () => (
  <section className="py-20 md:py-28 bg-white" data-testid="about-founder-section">
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="lg:col-span-7 relative">
        <div className="absolute -top-4 -left-4 w-2/3 h-2/3 border-2 border-[var(--yellow-2)] rounded-2xl" aria-hidden />
        <img src={C("founder-desk")} alt="Maaz Ahmad, Founder & MD of Royal Tours Group, at the Head Office" className="relative w-full aspect-[3/2] object-cover rounded-2xl shadow-2xl" data-testid="founder-photo" />
        <div className="absolute -bottom-8 right-4 md:right-8 w-32 md:w-40 rounded-xl overflow-hidden ring-4 ring-white shadow-xl">
          <img src={C("atm-dubai")} alt="Maaz Ahmad at Arabian Travel Market, Dubai" className="w-full aspect-[3/4] object-cover object-top" />
          <div className="bg-[var(--navy)] text-white text-[10px] tracking-widest uppercase text-center py-1.5">ATM Dubai</div>
        </div>
      </motion.div>
      <div className="lg:col-span-5">
        <Eyebrow>Leadership & Vision</Eyebrow>
        <h2 className="font-serif-display text-3xl md:text-[44px] leading-[1.1]">Meet our <span className="text-[var(--yellow-2)]">Founder</span></h2>
        <Quote className="w-10 h-10 text-[var(--yellow)] mt-8" />
        <p className="font-serif-display text-xl md:text-2xl leading-snug mt-3 text-[#0b2545]/90">
          Our promise has always been simple: every guest should travel with the confidence that someone who truly cares has planned every detail.
        </p>
        <p className="text-gray-600 mt-5 leading-relaxed">
          From a single desk in Golghar, Gorakhpur, Maaz Ahmad has built Royal Tours Group into an award-winning travel house — representing Uttar Pradesh at global trade shows and leading a team that has looked after more than a lakh travellers.
        </p>
        <div className="mt-8 flex items-center gap-4 border-t border-gray-200 pt-6">
          <div className="w-1 h-12 bg-[var(--yellow)] rounded-full" />
          <div>
            <div className="font-serif-display text-2xl" data-testid="founder-name">Maaz Ahmad</div>
            <div className="text-[12px] tracking-[0.2em] uppercase text-gray-500 mt-0.5" data-testid="founder-title">Founder & Managing Director</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const MOMENTS = [
  { icon: Trophy, img: "award-portrait", t: "India Travel Awards 2026", s: "Best Outbound Travel Company in Uttar Pradesh", pos: "object-top" },
  { icon: Plane, img: "atm-dubai", t: "Arabian Travel Market, Dubai", s: "Representing Royal Tours on the global stage", pos: "object-top" },
  { icon: BookOpen, img: "brochure-launch", t: "eBrochure Launch", s: "Unveiled with esteemed guests at our Head Office", pos: "object-center" },
  { icon: Mic, img: "media-interview", t: "In the News", s: "Media coverage of our journey and milestones", pos: "object-top" },
];

export const RecognitionSection = () => (
  <section className="py-20 md:py-24 bg-[#07182e] text-white relative overflow-hidden" data-testid="about-recognition-section">
    <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_85%_10%,#f5b800_0,transparent_40%)] pointer-events-none" />
    <div className="relative max-w-[1280px] mx-auto px-4 lg:px-6">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div className="max-w-2xl">
          <Eyebrow light>Recognition</Eyebrow>
          <h2 className="font-serif-display text-3xl md:text-[44px] leading-[1.1]">Honoured on stage, <span className="text-[var(--yellow)]">trusted on the road.</span></h2>
        </div>
      </div>
      <motion.figure variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative rounded-2xl overflow-hidden ring-1 ring-white/10" data-testid="award-feature">
        <img src={C("award-stage")} alt="Royal Tours Group receiving the India Travel Awards 2026" className="w-full aspect-[16/9] md:aspect-[21/9] object-cover object-[50%_35%]" />
        <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-10 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
          <div className="inline-flex items-center gap-2 bg-[var(--yellow)] text-[var(--navy)] text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full"><Trophy className="w-3.5 h-3.5" /> Winner · 2026</div>
          <div className="font-serif-display text-2xl md:text-4xl mt-3">India Travel Awards 2026</div>
          <div className="text-white/80 mt-1 text-sm md:text-base">Best Outbound Travel Company in Uttar Pradesh</div>
        </figcaption>
      </motion.figure>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-6">
        {MOMENTS.map((m, i) => (
          <motion.div key={m.t} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="group" data-testid={`moment-${i}`}>
            <div className="relative overflow-hidden rounded-xl aspect-[4/5]">
              <img src={C(m.img)} alt={m.t} loading="lazy" className={`w-full h-full object-cover ${m.pos} transition-transform duration-700 group-hover:scale-105`} />
              <span className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/95 text-[var(--navy)] flex items-center justify-center"><m.icon className="w-4 h-4" /></span>
            </div>
            <div className="font-semibold mt-3 text-[15px]">{m.t}</div>
            <div className="text-white/60 text-[13px] mt-0.5">{m.s}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const GALLERY = [
  { img: "office-reception", t: "Head Office reception, Golghar", span: "md:col-span-2 md:row-span-2" },
  { img: "office-cabin", t: "Founder's cabin", span: "" },
  { img: "team-ops", t: "Operations & ticketing team", span: "" },
  { img: "team-women", t: "Our holiday specialists", span: "" },
  { img: "team-meeting", t: "Planning sessions", span: "" },
  { img: "team-celebration", t: "Celebrating milestones together", span: "md:col-span-2" },
  { img: "office-shelf", t: "Mementos from around the world", span: "" },
];

export const OfficeGallery = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className="py-20 md:py-24 bg-white" data-testid="about-office-team-gallery">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <div className="max-w-2xl mb-10">
          <Eyebrow>Inside Royal Tours Group</Eyebrow>
          <h2 className="font-serif-display text-3xl md:text-[44px] leading-[1.1]">The people behind <span className="text-[var(--yellow-2)]">your journeys.</span></h2>
          <p className="text-gray-600 mt-4">Real faces, a real office. Drop by our Head Office in Golghar, Gorakhpur any day except Sunday.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] md:auto-rows-[220px] gap-3 md:gap-4">
          {GALLERY.map((g, i) => (
            <motion.button key={g.img} type="button" onClick={() => setOpen(g)} variants={fadeUp} custom={i % 4} initial="hidden" whileInView="show" viewport={{ once: true }}
              className={`group relative overflow-hidden rounded-xl text-left ${g.span}`} data-testid={`gallery-${g.img}`}>
              <img src={C(g.img)} alt={g.t} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-80 group-hover:opacity-100 transition-opacity" />
              <span className="absolute bottom-3 left-3 right-10 text-white text-[13px] font-semibold">{g.t}</span>
              <Expand className="absolute bottom-3 right-3 w-4 h-4 text-white/80 opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.button>
          ))}
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-5xl p-0 bg-black border-0 overflow-hidden [&>button]:hidden" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{open?.t}</DialogTitle>
          {open && <img src={C(open.img)} alt={open.t} className="w-full max-h-[80vh] object-contain" />}
          <div className="flex items-center justify-between px-5 py-3 text-white text-sm">
            <span>{open?.t}</span>
            <button type="button" onClick={() => setOpen(null)} data-testid="gallery-lightbox-close" className="p-1.5 rounded-full hover:bg-white/10" aria-label="Close"><X className="w-4 h-4" /></button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
